/**
 * Table Review Component
 * Review all defined tables, mark as ignored/relevant, and re-discover relationships
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  Table, Loader2, Check, Server, Database, Search, Ban, Eye, 
  RefreshCw, Link2, CheckSquare, Square, Filter, ChevronDown, Pencil, X, Save
} from 'lucide-react';
import { 
  getTableDefinitions, getIgnoredTables, ignoreTable, unignoreTable, 
  bulkIgnoreTables, discoverRelationships, saveTableDefinition
} from '../../services/api';

const TableReview = ({ isAdmin, onRelationshipsDiscovered }) => {
  const [state, setState] = useState({
    loading: true,
    tables: [],           // All defined tables with their status
    ignoredTables: [],    // List of ignored table keys
    selectedIds: [],      // Selected table IDs for bulk actions
    searchQuery: '',
    filterStatus: 'all',  // 'all', 'relevant', 'ignored'
    discovering: false,
    bulkActioning: false,
    editingTable: null,   // Table currently being edited
    editDefinition: ''    // Current edit value
  });

  useEffect(() => {
    loadAllTables();
  }, []);

  const loadAllTables = async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const [definitionsResult, ignoredResult] = await Promise.all([
        getTableDefinitions(),
        getIgnoredTables()
      ]);

      const definitions = definitionsResult.definitions || definitionsResult || [];
      const ignored = ignoredResult.ignored_tables || [];
      
      // Create a set of ignored table keys for quick lookup
      const ignoredKeys = new Set(
        ignored.map(t => `${t.server}|${t.database}|${t.table}`)
      );

      // Merge definitions with ignored status
      const allTables = definitions.map(def => ({
        ...def,
        key: `${def.server}|${def.database}|${def.table}`,
        isIgnored: ignoredKeys.has(`${def.server}|${def.database}|${def.table}`),
        ignoredReason: ignored.find(
          i => i.server === def.server && i.database === def.database && i.table === def.table
        )?.reason || ''
      }));

      // Also add ignored tables that might not have definitions
      ignored.forEach(ig => {
        const key = `${ig.server}|${ig.database}|${ig.table}`;
        if (!allTables.find(t => t.key === key)) {
          allTables.push({
            id: ig.id,
            server: ig.server,
            database: ig.database,
            table: ig.table,
            definition: ig.reason || 'No definition',
            key,
            isIgnored: true,
            ignoredReason: ig.reason || ''
          });
        }
      });

      setState(prev => ({
        ...prev,
        loading: false,
        tables: allTables,
        ignoredTables: ignored
      }));
    } catch (error) {
      console.error('Failed to load tables:', error);
      toast.error('Failed to load tables');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const filteredTables = state.tables.filter(t => {
    // Filter by status
    if (state.filterStatus === 'relevant' && t.isIgnored) return false;
    if (state.filterStatus === 'ignored' && !t.isIgnored) return false;
    
    // Filter by search
    if (state.searchQuery) {
      const query = state.searchQuery.toLowerCase();
      const tableName = (t.table || '').toLowerCase();
      const dbName = (t.database || '').toLowerCase();
      const serverName = (t.server || '').toLowerCase();
      const definition = (t.definition || '').toLowerCase();
      
      return (
        tableName.includes(query) ||
        dbName.includes(query) ||
        serverName.includes(query) ||
        definition.includes(query)
      );
    }
    return true;
  });

  const relevantCount = state.tables.filter(t => !t.isIgnored).length;
  const ignoredCount = state.tables.filter(t => t.isIgnored).length;

  const toggleSelect = (id) => {
    setState(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(id)
        ? prev.selectedIds.filter(i => i !== id)
        : [...prev.selectedIds, id]
    }));
  };

  const selectAll = () => {
    setState(prev => ({
      ...prev,
      selectedIds: filteredTables.map(t => t.id)
    }));
  };

  const deselectAll = () => {
    setState(prev => ({ ...prev, selectedIds: [] }));
  };

  const handleBulkIgnore = async () => {
    const tablesToIgnore = state.tables
      .filter(t => state.selectedIds.includes(t.id) && !t.isIgnored)
      .map(t => ({
        server: t.server,
        database: t.database,
        table: t.table,
        reason: 'Marked as not needed via bulk action'
      }));

    if (tablesToIgnore.length === 0) {
      toast.info('No relevant tables selected to ignore');
      return;
    }

    setState(prev => ({ ...prev, bulkActioning: true }));
    try {
      await bulkIgnoreTables(tablesToIgnore);
      toast.success(`Marked ${tablesToIgnore.length} tables as ignored`);
      setState(prev => ({ ...prev, selectedIds: [] }));
      await loadAllTables();
    } catch (error) {
      console.error('Bulk ignore failed:', error);
      toast.error('Failed to ignore tables');
    } finally {
      setState(prev => ({ ...prev, bulkActioning: false }));
    }
  };

  const handleBulkRestore = async () => {
    const tablesToRestore = state.tables.filter(
      t => state.selectedIds.includes(t.id) && t.isIgnored
    );

    if (tablesToRestore.length === 0) {
      toast.info('No ignored tables selected to restore');
      return;
    }

    setState(prev => ({ ...prev, bulkActioning: true }));
    try {
      for (const t of tablesToRestore) {
        await unignoreTable(t.server, t.database, t.table);
      }
      toast.success(`Restored ${tablesToRestore.length} tables as relevant`);
      setState(prev => ({ ...prev, selectedIds: [] }));
      await loadAllTables();
    } catch (error) {
      console.error('Bulk restore failed:', error);
      toast.error('Failed to restore tables');
    } finally {
      setState(prev => ({ ...prev, bulkActioning: false }));
    }
  };

  const handleSingleToggle = async (table) => {
    try {
      if (table.isIgnored) {
        await unignoreTable(table.server, table.database, table.table);
        toast.success(`Restored "${table.table}" as relevant`);
      } else {
        await ignoreTable(table.server, table.database, table.table, 'Marked as not needed');
        toast.success(`Marked "${table.table}" as ignored`);
      }
      await loadAllTables();
    } catch (error) {
      console.error('Toggle failed:', error);
      toast.error('Failed to update table status');
    }
  };

  const handleDiscoverRelationships = async () => {
    if (relevantCount === 0) {
      toast.error('No relevant tables to analyze');
      return;
    }

    setState(prev => ({ ...prev, discovering: true }));
    toast.info(`🔍 Discovering relationships for ${relevantCount} relevant tables...`, { duration: 5000 });

    try {
      const result = await discoverRelationships();
      if (result.success && result.relationships?.length > 0) {
        toast.success(`✅ Found ${result.relationships.length} table relationships!`, { duration: 5000 });
        if (onRelationshipsDiscovered) {
          onRelationshipsDiscovered(result.relationships);
        }
      } else {
        toast.info('No new relationships discovered', { duration: 3000 });
      }
    } catch (error) {
      console.error('Relationship discovery error:', error);
      toast.error('Relationship discovery failed');
    } finally {
      setState(prev => ({ ...prev, discovering: false }));
    }
  };

  // Edit definition handlers
  const startEditing = (table) => {
    setState(prev => ({
      ...prev,
      editingTable: table.key,
      editDefinition: table.definition || ''
    }));
  };

  const cancelEditing = () => {
    setState(prev => ({
      ...prev,
      editingTable: null,
      editDefinition: ''
    }));
  };

  const saveEditing = async (table) => {
    try {
      await saveTableDefinition(table.server, table.database, table.table, state.editDefinition);
      
      // Update local state
      setState(prev => ({
        ...prev,
        tables: prev.tables.map(t => 
          t.key === table.key ? { ...t, definition: state.editDefinition } : t
        ),
        editingTable: null,
        editDefinition: ''
      }));
      
      toast.success(`✅ Updated definition for ${table.table}`);
    } catch (error) {
      console.error('Save definition error:', error);
      toast.error('Failed to save definition');
    }
  };

  if (state.loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          <span className="ml-3 text-gray-600">Loading table definitions...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Table className="w-5 h-5 text-amber-600" />
            Table Review
            <span className="ml-2 text-sm font-normal text-gray-400">
              {state.tables.length} total
            </span>
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadAllTables}
              disabled={state.loading}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {isAdmin && relevantCount > 0 && (
              <Button 
                onClick={handleDiscoverRelationships}
                disabled={state.discovering}
                className="bg-blue-500 hover:bg-blue-600"
                size="sm"
              >
                {state.discovering ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                Re-discover Relationships
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Stats Bar */}
        <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-green-600" />
            <span className="text-sm">
              <strong className="text-green-700">{relevantCount}</strong> Relevant
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-600" />
            <span className="text-sm">
              <strong className="text-red-700">{ignoredCount}</strong> Ignored
            </span>
          </div>
          {state.selectedIds.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-purple-600 font-medium">
                {state.selectedIds.length} selected
              </span>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search tables..."
              value={state.searchQuery}
              onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={state.filterStatus === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setState(prev => ({ ...prev, filterStatus: 'all' }))}
            >
              All
            </Button>
            <Button
              variant={state.filterStatus === 'relevant' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setState(prev => ({ ...prev, filterStatus: 'relevant' }))}
              className={state.filterStatus === 'relevant' ? 'bg-green-500 hover:bg-green-600' : ''}
            >
              <Eye className="w-3 h-3 mr-1" />
              Relevant
            </Button>
            <Button
              variant={state.filterStatus === 'ignored' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setState(prev => ({ ...prev, filterStatus: 'ignored' }))}
              className={state.filterStatus === 'ignored' ? 'bg-red-500 hover:bg-red-600' : ''}
            >
              <Ban className="w-3 h-3 mr-1" />
              Ignored
            </Button>
          </div>
        </div>

        {/* Bulk Actions */}
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 rounded-lg border">
            <Button variant="outline" size="sm" onClick={selectAll}>
              <CheckSquare className="w-3 h-3 mr-1" />
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAll}>
              <Square className="w-3 h-3 mr-1" />
              Deselect
            </Button>
            <div className="h-4 w-px bg-gray-300 mx-2" />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleBulkIgnore}
              disabled={state.bulkActioning || state.selectedIds.length === 0}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              {state.bulkActioning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
              Mark as Ignored
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleBulkRestore}
              disabled={state.bulkActioning || state.selectedIds.length === 0}
              className="border-green-200 text-green-600 hover:bg-green-50"
            >
              {state.bulkActioning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
              Mark as Relevant
            </Button>
          </div>
        )}

        {/* Table List */}
        {filteredTables.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Table className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No tables found</p>
            <p className="text-sm text-gray-400 mt-1">
              {state.tables.length === 0 
                ? 'Define some tables first in Phase 3' 
                : 'Try adjusting your search or filter'}
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="w-10 p-3 text-left">
                      <input 
                        type="checkbox" 
                        checked={state.selectedIds.length === filteredTables.length && filteredTables.length > 0}
                        onChange={() => state.selectedIds.length === filteredTables.length ? deselectAll() : selectAll()}
                        className="w-4 h-4 rounded"
                      />
                    </th>
                    <th className="p-3 text-left font-medium text-gray-600">Server</th>
                    <th className="p-3 text-left font-medium text-gray-600">Database</th>
                    <th className="p-3 text-left font-medium text-gray-600">Table</th>
                    <th className="p-3 text-left font-medium text-gray-600">Definition / Comment</th>
                    <th className="p-3 text-left font-medium text-gray-600 w-28">Status</th>
                    {isAdmin && <th className="p-3 text-left font-medium text-gray-600 w-24">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredTables.map((table) => (
                    <tr 
                      key={table.id || table.key} 
                      className={`hover:bg-gray-50 ${table.isIgnored ? 'bg-red-50/30' : ''}`}
                    >
                      <td className="p-3">
                        <input 
                          type="checkbox" 
                          checked={state.selectedIds.includes(table.id)}
                          onChange={() => toggleSelect(table.id)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                      <td className="p-3">
                        <div className="text-gray-600 text-xs">{table.server}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-gray-600">{table.database}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-gray-800">{table.table}</div>
                      </td>
                      <td className="p-3">
                        {state.editingTable === table.key ? (
                          <div className="flex flex-col gap-2">
                            <Textarea
                              value={state.editDefinition}
                              onChange={(e) => setState(prev => ({ ...prev, editDefinition: e.target.value }))}
                              className="min-h-[80px] text-sm"
                              placeholder="Enter table definition..."
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => saveEditing(table)} className="bg-green-500 hover:bg-green-600">
                                <Save className="w-3 h-3 mr-1" /> Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEditing}>
                                <X className="w-3 h-3 mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2">
                            <p className="text-gray-600 line-clamp-2 max-w-md flex-1" title={table.definition}>
                              {table.definition || <span className="text-gray-400 italic">No definition</span>}
                            </p>
                            {isAdmin && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => startEditing(table)}
                                className="text-blue-500 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        {table.isIgnored ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                            <Ban className="w-3 h-3" />
                            Ignored
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            <Eye className="w-3 h-3" />
                            Relevant
                          </span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSingleToggle(table)}
                            className={table.isIgnored 
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                              : 'text-red-600 hover:text-red-700 hover:bg-red-50'}
                          >
                            {table.isIgnored ? (
                              <><Eye className="w-3 h-3 mr-1" /> Restore</>
                            ) : (
                              <><Ban className="w-3 h-3 mr-1" /> Ignore</>
                            )}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Tip:</strong> Tables marked as "Ignored" will be excluded from AI queries. 
            The AI will never search or reference data from ignored tables. 
            Click "Re-discover Relationships" after making changes to update the AI's understanding.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TableReview;
